import { useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAppSelector } from '../../../app/hooks';
import { getOrgBasedUsers } from '../../../store/investor/apiHelpers';
import { fetchOrgInfoById } from '../../../store/investor/investorApiHelper';
import { USER_TYPE } from '../../../utils/constants/user';
import { updateQueryParams } from '../../../utils/searchParamUtils';
import { isEmpty } from '../../../utils/utils';
import { useVisibilityConfig } from '../../../utils/useVisibilityConfig';
import {
    useLeadDynamicDropdownOptions,
    useLeadFieldCellDisabled,
    useLeadFieldUpdaters,
    useLeadsColumnsConfig,
} from '../../../modules/Investor/Portfolio/components/CompaniesList/useLeadEditing';
import { validOrgId } from '../../MagicUpload/Utils/MagicUploadUtils';
import { StatusDropdownOption } from '../../_custom/StatusDropdown/StatusDropdown';
import { ADD_PARTNER_ALLOWED_ACCOUNT_TYPES, ADD_PARTNER_MESSAGES, LEAD_PARTNER_FIELD } from '../NeedInfoV2Constants';

type LeadPartnerState = 'UNKNOWN' | 'UNTAGGED' | 'TAGGED';

interface PartnerOrg {
    organizationId: string;
    organizationName?: string;
    organizationDisplayName: string;
    userData?: { emailId?: string }[];
}

interface UseAddPartnerInput {
    orgId?: string | null;
    isBorrowerMode: boolean;
    canSeeUserList: boolean;
    getCombinedOrgUserList: (partnerOrgIdOverride?: string) => Promise<void>;
}

const normalize = (value?: string) => (value ?? '').trim().toLowerCase();

const findOrgByEmail = (orgs: PartnerOrg[], email: string) =>
    orgs.find((org) => org?.userData?.some((u) => normalize(u?.emailId) === normalize(email)));

const useAddPartner = ({ orgId, isBorrowerMode, canSeeUserList, getCombinedOrgUserList }: UseAddPartnerInput) => {
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const userData = useAppSelector((state) => state.user.userData);
    const platformData = useAppSelector((state) => state.user.domainMetadata);
    const partnerData = useAppSelector((state) => state.investor.partnerData) as PartnerOrg[];
    const { isConfigLoaded } = useVisibilityConfig();
    const getLeadsFieldCellDisabled = useLeadFieldCellDisabled();
    const { updateSingleSelectDropdown } = useLeadFieldUpdaters({ owners: [], userName: '' });

    const [leadPartnerState, setLeadPartnerState] = useState<LeadPartnerState>('UNKNOWN');
    const [isAddPartnerModalOpen, setIsAddPartnerModalOpen] = useState(false);
    const [taggedPartnerOrgId, setTaggedPartnerOrgId] = useState<string | undefined>();

    const { accountType, primaryEmail, investorOrgId } = userData;
    const platformInvestorOrgId = platformData?.investorOrgId;

    // An undefined investorOrgId makes the hook skip the fetch, so only the gated callers load the config.
    const canLoadColumnsConfig = ADD_PARTNER_ALLOWED_ACCOUNT_TYPES.includes(accountType) && isBorrowerMode && canSeeUserList;
    const { leadsColumnsConfig } = useLeadsColumnsConfig({
        investorOrgId: canLoadColumnsConfig ? platformInvestorOrgId : undefined,
        accountType,
        primaryEmail,
    });
    const partnerColumn = leadsColumnsConfig.columns.find((c) => c?.field === LEAD_PARTNER_FIELD);

    // Same predicate as the leads-list Partner Name cell's edit gate.
    const canEditLeadPartner =
        ADD_PARTNER_ALLOWED_ACCOUNT_TYPES.includes(accountType) &&
        isConfigLoaded &&
        !!partnerColumn &&
        !getLeadsFieldCellDisabled(partnerColumn.disable);
    const shouldCheck = canEditLeadPartner && isBorrowerMode && canSeeUserList && !!orgId;

    useEffect(() => {
        setLeadPartnerState('UNKNOWN');
        setTaggedPartnerOrgId(undefined);
        if (!shouldCheck) return;
        let isStale = false;
        fetchOrgInfoById({ orgId: orgId as string }).then((info) => {
            if (isStale) return;
            if (isEmpty(info)) return setLeadPartnerState('UNKNOWN');
            const storedPartnerOrgId = info?.customFields?.partnerOrgId;
            if (isEmpty(storedPartnerOrgId)) return setLeadPartnerState('UNTAGGED');
            setLeadPartnerState('TAGGED');
            setTaggedPartnerOrgId(storedPartnerOrgId);
            if (searchParams.get('partnerOrgId') !== storedPartnerOrgId) {
                updateQueryParams(searchParams, setSearchParams, { partnerOrgId: storedPartnerOrgId }, location);
                getCombinedOrgUserList(storedPartnerOrgId);
            }
        });
        return () => {
            isStale = true;
        };
    }, [shouldCheck, orgId]);

    const showAddPartner = shouldCheck && leadPartnerState === 'UNTAGGED';

    const { partnerNameDropdownOptions } = useLeadDynamicDropdownOptions({ partnerData, owners: undefined });
    const partnerOptions = useMemo(() => Object.values(partnerNameDropdownOptions) as StatusDropdownOption[], [partnerNameDropdownOptions]);

    // A partner user's own organizationId is not the partner org's id, so the header resolves from the lead's tag.
    const partnerOrgId = taggedPartnerOrgId || validOrgId(searchParams.get('partnerOrgId'));

    const getPartnerName = (id?: string | null) => (id ? partnerNameDropdownOptions[id]?.label : undefined);

    const tagPartner = (option: Pick<StatusDropdownOption, 'value'>, message?: string) =>
        updateSingleSelectDropdown({
            row: { assesseeOrgId: orgId },
            option,
            column: partnerColumn,
            handlers: {
                onSuccess: () => {
                    setLeadPartnerState('TAGGED');
                    setTaggedPartnerOrgId(option.value as string);
                    updateQueryParams(searchParams, setSearchParams, { partnerOrgId: option.value }, location);
                    getCombinedOrgUserList(option.value);
                    toast.success(message ?? ADD_PARTNER_MESSAGES.TAG_SUCCESS);
                },
            },
        });

    const handleBeforeInvite = ({ email, orgDisplayName }: { email: string; orgDisplayName: string }) => {
        const orgs = Array.isArray(partnerData) ? partnerData : [];
        const emailMatch = findOrgByEmail(orgs, email);
        const nameMatch = orgs.find(
            (org) =>
                !!normalize(orgDisplayName) &&
                (normalize(org?.organizationName) === normalize(orgDisplayName) ||
                    normalize(org?.organizationDisplayName) === normalize(orgDisplayName)),
        );
        const match = emailMatch ?? nameMatch;
        if (!match?.organizationId) return false;
        const label = match.organizationDisplayName || match.organizationName || '';
        const message = emailMatch
            ? `${label}${ADD_PARTNER_MESSAGES.ALREADY_EXISTS_SUFFIX}`
            : `${label}${ADD_PARTNER_MESSAGES.ALREADY_EXISTS_SUFFIX} ${email}${ADD_PARTNER_MESSAGES.NAME_MATCH_NOT_ADDED_SUFFIX}`;
        tagPartner({ value: match.organizationId }, message);
        return true;
    };

    const handleInvited = async (email: string) => {
        const res = await getOrgBasedUsers({ orgId: investorOrgId, userType: [USER_TYPE.PARTNER], orgName: '', getPaginatedData: false });
        const created = Array.isArray(res?.data) ? findOrgByEmail(res.data as PartnerOrg[], email) : undefined;
        if (created?.organizationId) tagPartner({ value: created.organizationId });
        else toast.error(ADD_PARTNER_MESSAGES.CREATED_NOT_TAGGED);
    };

    return {
        showAddPartner,
        partnerOptions,
        getPartnerName,
        partnerOrgId,
        tagPartner,
        isAddPartnerModalOpen,
        openAddPartnerModal: () => setIsAddPartnerModalOpen(true),
        closeAddPartnerModal: () => setIsAddPartnerModalOpen(false),
        handleBeforeInvite,
        handleInvited,
    };
};

export default useAddPartner;
